// Core
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Chip, Crumbs, DataTable, KpiStrip, PageHead } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  contractsApiSlice,
  useCloneContractMutation,
  useDeleteContractByIdMutation,
} from "state/contracts/contractsApiSlice";
import { selectAllMeters, useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";

// Utils
import { ciclesTypes, contractTypes } from "utils/constants/index";

const resolveLabel = (arr, value, t, keyPrefix) => {
  const found = arr.find((o) => o.value === value);
  if (!found) return value;
  return t(`${keyPrefix}.${value}`, { defaultValue: found.label });
};

/* Global list of every tariff contract in the community. The API only exposes
   contracts per meter, so rows are aggregated client-side across all CPE /
   aggregator meters (same approach as the community export). */
function ListAllContracts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["contracts", "meters", "common"]);

  const { meters } = useGetMetersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ meters: (data && selectAllMeters(data)) || [] }),
    }
  );

  const contractMeters = useMemo(
    () => meters.filter((m) => m.type === "CPE" || m.type === "AGGREGATOR_METER"),
    [meters]
  );

  const [rowsRaw, setRowsRaw] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        contractMeters.map((meter) =>
          dispatch(
            contractsApiSlice.endpoints.getContractsByMeterId.initiate(
              { communityId: community.id, meterId: meter.id },
              { forceRefetch: true }
            )
          )
        )
      );
      if (cancelled) return;
      const all = results.flatMap((res, i) =>
        (res.data || []).map((c) => ({ ...c, meterRef: contractMeters[i] }))
      );
      setRowsRaw(all);
    })();
    return () => {
      cancelled = true;
    };
  }, [contractMeters, community.id, dispatch, reloadKey]);

  // CLONE / DELETE
  const [cloneContract] = useCloneContractMutation();
  const [deleteContract] = useDeleteContractByIdMutation();
  const onClone = (contractId, meterId) => {
    dispatch(
      openWarningAlert({
        confirmText: t("confirm.clone"),
        confirmAction: async () => {
          try {
            await cloneContract({ communityId: community.id, contractId, meterId }).unwrap();
            reload();
          } catch (err) {
            dispatch(openNotificationPopup({ error: err }));
          }
        },
      })
    );
  };
  const onDelete = (contractId, meterId) => {
    dispatch(
      openWarningAlert({
        confirmText: t("confirm.delete"),
        confirmAction: async () => {
          try {
            await deleteContract({ contractId, meterId, communityId: community.id }).unwrap();
            reload();
          } catch (err) {
            dispatch(openNotificationPopup({ error: err }));
          }
        },
      })
    );
  };

  const openContract = (contractId, meter, action) =>
    navigate(`/communities/${community.id}/infrastructure/contracts/${contractId}`, {
      state: { meterId: meter.id, action, meterIdentifier: meter.identifier },
    });

  const rows = useMemo(
    () =>
      rowsRaw.map((c) => ({
        id: c.id,
        meter: c.meterRef.identifier,
        meterId: c.meterRef.id,
        meterEntity: c.meterRef,
        validity: c.validity,
        buy_type: c.buy_type,
        sell_type: c.sell_type,
        tariff_cycle: c.tariff_cycle,
        contracted_power: c.contracted_power,
      })),
    [rowsRaw]
  );

  const columns = [
    {
      key: "meter",
      label: t("list.table.columns.meter"),
      render: (v) => (
        <div className="rec-cell-member">
          <div
            className="rec-av-sm"
            style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}
          >
            <Icon name="receipt_long" style={{ fontSize: 18 }} />
          </div>
          <span className="font-[family-name:var(--mono)] text-[12.5px] text-ink">{v}</span>
        </div>
      ),
    },
    { key: "validity", label: t("list.table.columns.validity") },
    {
      key: "buy_type",
      label: t("list.table.columns.buy_type"),
      render: (v) => <Chip tone="teal">{resolveLabel(contractTypes, v, t, "options.types")}</Chip>,
    },
    {
      key: "sell_type",
      label: t("list.table.columns.sell_type"),
      render: (v) => <Chip tone="info">{resolveLabel(contractTypes, v, t, "options.types")}</Chip>,
    },
    {
      key: "tariff_cycle",
      label: t("list.table.columns.tariff_cycle"),
      render: (v) => resolveLabel(ciclesTypes, v, t, "options.cicles"),
    },
    {
      key: "contracted_power",
      label: t("list.table.columns.contracted_power"),
      align: "right",
      render: (v) => <span className="tabular-nums">{v}</span>,
    },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <div className="rec-row-actions">
          <button
            type="button"
            className="rec-icon-btn"
            title={t("list.actions.view")}
            onClick={(e) => {
              e.stopPropagation();
              openContract(row.id, row.meterEntity, "view");
            }}
          >
            <Icon name="visibility" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("list.actions.clone")}
            onClick={(e) => {
              e.stopPropagation();
              onClone(row.id, row.meterId);
            }}
          >
            <Icon name="content_copy" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("list.actions.edit")}
            onClick={(e) => {
              e.stopPropagation();
              openContract(row.id, row.meterEntity, "edit");
            }}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("list.actions.delete")}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row.id, row.meterId);
            }}
          >
            <Icon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  const metersWithContracts = new Set(rowsRaw.map((c) => c.meterRef.id)).size;

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="contracts" />
      <Crumbs
        items={[
          { label: community?.name || "—" },
          {
            label: t("sidenav.infrastructure", { ns: "community", defaultValue: "Infraestrutura" }),
          },
          { label: t("all.title") },
        ]}
      />
      <PageHead title={t("all.title")} subtitle={t("all.subtitle")} />

      <KpiStrip
        className="mb-rec"
        cols={4}
        items={[
          {
            icon: "receipt_long",
            value: String(rowsRaw.length),
            label: t("all.stats.total"),
            tint: "teal",
          },
          {
            icon: "lock",
            value: String(rowsRaw.filter((c) => c.buy_type === "FIXED").length),
            label: t("all.stats.fixed"),
            tint: "amber",
          },
          {
            icon: "trending_up",
            value: String(rowsRaw.filter((c) => (c.buy_type || "").startsWith("INDEXED")).length),
            label: t("all.stats.indexed"),
            tint: "info",
          },
          {
            icon: "electric_meter",
            value: String(metersWithContracts),
            label: t("all.stats.meters"),
            tint: "green",
          },
        ]}
      />

      <div className="rec-card">
        <DataTable
          columns={columns}
          rows={rows}
          onRow={(row) => openContract(row.id, row.meterEntity, "view")}
          search={t("all.search")}
          searchKeys={["meter", "validity", "buy_type", "sell_type"]}
        />
      </div>
    </div>
  );
}

export default ListAllContracts;
